import { useHandler } from "@redotech/react-util/hook";
import { PageContext } from "@redotech/redo-model/agents/page-context";
import { RedoInternalProduct } from "@redotech/redo-model/redo-internal-products";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { nativeRandom, shuffle } from "@redotech/util/random";
import { createContext, useContext, useMemo, useState } from "react";
import { getActiveProductNames } from "./active-products";

export interface GlobalAgentContextType {
  pageContext?: PageContext;
  setPageContext: (context: PageContext | undefined) => void;
  defaultSuggestedQuestions: string[];
  onPageDataChanged?: () => void;
  setOnPageDataChanged: (callback: (() => void) | undefined) => void;
}

export const GlobalAgentContext = createContext<GlobalAgentContextType>({
  pageContext: undefined,
  setPageContext: () => {},
  defaultSuggestedQuestions: [],
  onPageDataChanged: undefined,
  setOnPageDataChanged: () => {},
});

export interface GlobalAgentProviderProps {
  children: React.ReactNode;
}

export function GlobalAgentProvider({ children }: GlobalAgentProviderProps) {
  const [pageContext, setPageContext] = useState<PageContext | undefined>(
    undefined,
  );
  const [onPageDataChanged, setOnPageDataChanged] = useState<
    (() => void) | undefined
  >(undefined);

  const defaultSuggestedQuestions = useDefaultSuggestedQuestions();

  const stableSetOnPageDataChanged = useHandler(
    (cb: (() => void) | undefined) => setOnPageDataChanged(() => cb),
  );

  const contextValue = useMemo(
    () => ({
      pageContext,
      setPageContext,
      defaultSuggestedQuestions,
      onPageDataChanged,
      setOnPageDataChanged: stableSetOnPageDataChanged,
    }),
    [
      pageContext,
      defaultSuggestedQuestions,
      onPageDataChanged,
      stableSetOnPageDataChanged,
    ],
  );

  return (
    <GlobalAgentContext.Provider value={contextValue}>
      {children}
    </GlobalAgentContext.Provider>
  );
}

function useDefaultSuggestedQuestions(): string[] {
  const team = useContext(TeamContext);

  return useMemo(() => {
    const activeProducts = getActiveProductNames(team?.redoInternalProducts);

    const suggestedQuestions: string[] = [];

    if (activeProducts.includes(RedoInternalProduct.RETURNS)) {
      suggestedQuestions.push(
        "What were my most returned products over the last 90 days?",
        "What were the most common return reasons last month?",
      );
    }
    if (activeProducts.includes(RedoInternalProduct.PACKAGE_PROTECTION)) {
      suggestedQuestions.push(
        "What percentage of my orders are eligible for package protection?",
        "How many orders had package protection last week?",
      );
    }

    return shuffle(suggestedQuestions, nativeRandom).slice(0, 3);
  }, [team?.redoInternalProducts]);
}
